import apiClient from './api';
import { Article, PaginatedArticlesResponse } from '../types/news.types';

/**
 * News Service - Handles all article-related API calls
 */
class NewsService {
  /**
   * Get all articles with filters
   */
  async getAllArticles(params?: {
    page?: number;
    limit?: number;
    category?: string;
    status?: string;
    search?: string;
    isFeatured?: boolean;
    isBreakingNews?: boolean;
    sortBy?: string;
    order?: 'asc' | 'desc';
  }): Promise<{ articles: Article[]; pagination: any }> {
    try {
      console.log('📰 Fetching articles with params:', params);
      const response = await apiClient.get('/articles', { params });
      console.log('📰 getAllArticles response:', response.data);
      
      if (!response.data?.data?.articles) {
        console.warn('⚠️ Unexpected response structure:', response.data);
        return { articles: [], pagination: {} };
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('❌ getAllArticles error:', error);
      console.error('Error details:', error.message, error.response?.status);
      throw error;
    }
  }

  /**
   * Get articles by specific category
   */
  async getArticlesByCategory(
    category: string,
    page: number = 1,
    limit: number = 10
  ): Promise<{ articles: Article[]; pagination: any }> {
    try {
      console.log(`📂 Fetching ${category} articles - page ${page}, limit ${limit}`);
      const response = await apiClient.get(`/articles/category/${category}`, {
        params: { page, limit },
      });
      console.log(`📂 getArticlesByCategory (${category}):`, response.data);
      
      if (!response.data?.data?.articles) {
        console.warn('⚠️ Unexpected response structure:', response.data);
        return { articles: [], pagination: {} };
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('❌ getArticlesByCategory error:', error);
      console.error('Error details:', error.message, error.response?.status);
      throw error;
    }
  }

  /**
   * Get top/featured articles for hero section
   */
  async getFeaturedArticles(limit: number = 5): Promise<Article[]> {
    try {
      console.log('⭐ Fetching featured articles, limit:', limit);
      const response = await apiClient.get('/articles/featured/top', {
        params: { limit },
      });
      console.log('⭐ getFeaturedArticles response:', response.data);
      
      if (!response.data?.data?.articles) {
        console.warn('⚠️ Unexpected response structure:', response.data);
        return [];
      }
      
      return response.data.data.articles;
    } catch (error: any) {
      console.error('❌ getFeaturedArticles error:', error);
      console.error('Error details:', error.message, error.response?.status);
      throw error;
    }
  }

  /**
   * Get single article by slug
   */
  async getArticleBySlug(slug: string): Promise<Article> {
    try {
      console.log('📄 Fetching article with slug:', slug);
      const response = await apiClient.get(`/articles/${slug}`);
      console.log('📄 getArticleBySlug response:', response.data);
      
      if (!response.data?.data?.article) {
        console.warn('⚠️ Unexpected response structure:', response.data);
        throw new Error('Article not found');
      }
      
      return response.data.data.article;
    } catch (error: any) {
      console.error('❌ getArticleBySlug error:', error);
      console.error('Error details:', error.message, error.response?.status);
      throw error;
    }
  }

  /**
   * Like/Unlike article
   */
  async toggleLike(articleId: string): Promise<{ likes: number; isLiked: boolean }> {
    try {
      console.log('❤️ Toggling like for article:', articleId);
      const response = await apiClient.post(`/articles/${articleId}/like`);
      console.log('❤️ toggleLike response:', response.data);
      
      if (!response.data?.data) {
        console.warn('⚠️ Unexpected response structure:', response.data);
        return { likes: 0, isLiked: false };
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('❌ toggleLike error:', error);
      console.error('Error details:', error.message, error.response?.status);
      throw error;
    }
  }

  /**
   * Search articles
   */
  async searchArticles(
    query: string,
    page: number = 1,
    limit: number = 10
  ): Promise<{ articles: Article[]; pagination: any }> {
    try {
      console.log('🔍 Searching articles with query:', query, 'page:', page);
      const response = await apiClient.get('/articles', {
        params: { search: query, page, limit },
      });
      console.log('🔍 searchArticles response:', response.data);
      
      if (!response.data?.data?.articles) {
        console.warn('⚠️ Unexpected response structure:', response.data);
        return { articles: [], pagination: {} };
      }
      
      return response.data.data;
    } catch (error: any) {
      console.error('❌ searchArticles error:', error);
      console.error('Error details:', error.message, error.response?.status);
      throw error;
    }
  }
}

export default new NewsService();